/**
 * @fileoverview The text-to-speech providers — their vocabulary, declared once (REGISTER-WEB-ORDERS-001, the TTS ONE
 * vocabulary).
 *
 * ## Why it lives here
 *
 * The provider names were written as literals across PortPuffin: two identical `VALID_PROVIDERS` sets (port-api's usage
 * meter and its cost rates), the voice validators and the phone-identity schema, the orchestrator's fallback chain, the
 * cloning service, the breaker map, the default-provider flag, `@puffin-core/voice-communication`'s provider factory
 * (which dispatches on them) and port-web's voice-catalog filter. A provider added or renamed in one place left the rest
 * behind. port-api, port-web and the factory read them from here; this package is dual-use, so the browser reads the
 * same names.
 *
 * ## The cuts
 *
 * - every provider (`TTS_PROVIDER_VALUES`): what the factory dispatches, the usage meter records and its query admits;
 *   `mock` is the test and fallback provider, metered at no cost;
 * - the LIVE providers (`LIVE_TTS_PROVIDERS`): the two a tenant's voice can name (a phone identity's voice, the voice
 *   catalog, the orchestrator's chain), never the mock;
 * - the default (`DEFAULT_TTS_PROVIDER`): AWS Polly, the provider a voice uses when it names none;
 * - the cloning provider (`VOICE_CLONING_PROVIDER`): ElevenLabs, the one provider that clones a voice.
 *
 * Applied migrations keep their own literals: they are history, frozen as written.
 *
 * @module @puffin-core/status-taxonomy/ttsProvider
 */

'use strict';

/**
 * The text-to-speech providers.
 *
 * @type {Readonly<Object<string, string>>}
 */
const TTS_PROVIDER = Object.freeze({
  POLLY: 'polly',
  ELEVENLABS: 'elevenlabs',
  MOCK: 'mock',
});

/**
 * Every provider, in declaration order.
 *
 * @type {ReadonlyArray<string>}
 */
const TTS_PROVIDER_VALUES = Object.freeze(Object.values(TTS_PROVIDER));

/**
 * The providers a tenant's voice can name.
 *
 * @type {ReadonlyArray<string>}
 */
const LIVE_TTS_PROVIDERS = Object.freeze([TTS_PROVIDER.POLLY, TTS_PROVIDER.ELEVENLABS]);

/** The provider a voice uses when it names none. */
const DEFAULT_TTS_PROVIDER = TTS_PROVIDER.POLLY;

/** The one provider that clones a voice. */
const VOICE_CLONING_PROVIDER = TTS_PROVIDER.ELEVENLABS;

module.exports = {
  TTS_PROVIDER,
  TTS_PROVIDER_VALUES,
  LIVE_TTS_PROVIDERS,
  DEFAULT_TTS_PROVIDER,
  VOICE_CLONING_PROVIDER,
};
